import type { DeviceKind } from './adapter'

export interface DeviceInfo {
  deviceId: string
  label: string
  kind: DeviceKind
}

const KNOWN_KINDS: DeviceKind[] = ['audioinput', 'videoinput', 'audiooutput']

function isKnownKind(kind: MediaDeviceKind): kind is DeviceKind {
  return (KNOWN_KINDS as string[]).includes(kind)
}

export async function enumerateDevices(): Promise<DeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return []
  const devices = await navigator.mediaDevices.enumerateDevices()
  const result: DeviceInfo[] = []
  for (const d of devices) {
    if (isKnownKind(d.kind)) {
      result.push({ deviceId: d.deviceId, label: d.label, kind: d.kind })
    }
  }
  return result
}

// Лейблы устройств пусты, пока не выдан permission — поэтому запрос доступа
// предшествует перечислению в prejoin-потоке.
export async function requestMediaPermissions(constraints: {
  audio?: boolean
  video?: boolean
}): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: constraints.audio ?? false,
    video: constraints.video ?? false,
  })
}

// «Системное по умолчанию» конкретным id: Chromium держит псевдо-устройство
// `default`, которое само следует за системным выбором (воткнули гарнитуру —
// переехало), остальные браузеры отдают дефолтное первым в списке.
export async function systemDefaultDeviceId(kind: DeviceKind): Promise<string | undefined> {
  const devices = await enumerateDevices().catch(() => [] as DeviceInfo[])
  const ofKind = devices.filter((d) => d.kind === kind)
  return ofKind.find((d) => d.deviceId === 'default')?.deviceId ?? ofKind[0]?.deviceId
}

export function onDeviceChange(handler: () => void): () => void {
  const target = navigator.mediaDevices
  if (!target) return () => {}
  target.addEventListener('devicechange', handler)
  return () => target.removeEventListener('devicechange', handler)
}
